import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Dimensions,
  type StyleProp,
  type TextProps,
  type ViewStyle,
} from 'react-native';
import { colors, spacing } from '../constants/theme';
import { HAMBURGER_ICONS } from '../constants/hamburgerIcons';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const AnimatedTouchableOpacity = Animated.createAnimatedComponent(TouchableOpacity);

interface IconComponentProps extends TextProps {
  name: string;
  size?: number;
  color?: string;
}

// Import MaterialCommunityIcons conditionally for web compatibility
let MaterialCommunityIcons: React.ComponentType<IconComponentProps>;
try {
  const ExpoVectorIcons = require('@expo/vector-icons') as {
    MaterialCommunityIcons: React.ComponentType<IconComponentProps>;
  };
  MaterialCommunityIcons = ExpoVectorIcons.MaterialCommunityIcons;
} catch (e) {
  // Fallback for web - create a simple text-based icon component
  const { Text } = require('react-native') as typeof import('react-native');
  MaterialCommunityIcons = ({ name, size = 24, color, style, ...props }) => {
    // Build icon map from hamburger icons registry + static icons
    const iconMap: Record<string, string> = {
      'menu': '☰',
      'close': '✕',
      'magnify': '🔍',
      'plus': '+',
      'note-text': '✍️',
      'hammer-wrench': '🛠️',
    };

    // Merge hamburger icons
    HAMBURGER_ICONS.forEach(icon => {
      iconMap[icon.iconName] = icon.webFallback;
    });

    return (
      <Text
        style={[
          {
            fontSize: size,
            color,
            fontFamily: 'system-ui, -apple-system, "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif',
          },
          style,
        ]}
        {...props}
      >
        {iconMap[name] || '•'}
      </Text>
    );
  };
}

interface ActionItem {
  id: string;  // Stable unique identifier (used as React key)
  icon: string;
  label: string;
  onPress: () => void;
  subActions?: ActionItem[];  // For category expansion (horizontal)
}

interface ExpandableFABProps {
  actions: ActionItem[];
  style?: StyleProp<ViewStyle>;
}

export const ExpandableFAB: React.FC<ExpandableFABProps> = ({ actions, style }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategoryIndex, setExpandedCategoryIndex] = useState<number | null>(null);
  const [animation] = useState(new Animated.Value(0));
  const [horizontalAnimation] = useState(new Animated.Value(0));
  const openContainerHeight = Math.min(screenHeight - spacing.md * 2, (actions.length + 1) * 70);
  const openContainerWidth = Math.min(screenWidth - spacing.md * 2, 280);

  const toggleMenu = () => {
    const toValue = isOpen ? 0 : 1;
    setIsOpen(!isOpen);

    // Close horizontal expansion when closing main menu
    if (isOpen) {
      setExpandedCategoryIndex(null);
      Animated.parallel([
        Animated.spring(animation, {
          toValue,
          useNativeDriver: false,
          tension: 68,
          friction: 8,
        }),
        Animated.spring(horizontalAnimation, {
          toValue: 0,
          useNativeDriver: false,
          tension: 68,
          friction: 8,
        }),
      ]).start();
    } else {
      Animated.spring(animation, {
        toValue,
        useNativeDriver: false,
        tension: 68,
        friction: 8,
      }).start();
    }
  };

  const handleActionPress = (action: ActionItem, index: number) => {
    // If action has subActions, toggle horizontal expansion
    if (action.subActions && action.subActions.length > 0) {
      const shouldExpand = expandedCategoryIndex !== index;
      setExpandedCategoryIndex(shouldExpand ? index : null);

      Animated.spring(horizontalAnimation, {
        toValue: shouldExpand ? 1 : 0,
        useNativeDriver: false,
        tension: 68,
        friction: 8,
      }).start();
      return;
    }

    // Regular action - execute and close menu.
    action.onPress();
    setIsOpen(false);
    setExpandedCategoryIndex(null);
    Animated.parallel([
      Animated.spring(animation, {
        toValue: 0,
        useNativeDriver: false,
        tension: 68,
        friction: 8,
      }),
      Animated.spring(horizontalAnimation, {
        toValue: 0,
        useNativeDriver: false,
        tension: 68,
        friction: 8,
      }),
    ]).start();

  };

  const handleSubActionPress = (subAction: ActionItem) => {
    subAction.onPress();
    setIsOpen(false);
    setExpandedCategoryIndex(null);
    Animated.parallel([
      Animated.spring(animation, {
        toValue: 0,
        useNativeDriver: false,
        tension: 68,
        friction: 8,
      }),
      Animated.spring(horizontalAnimation, {
        toValue: 0,
        useNativeDriver: false,
        tension: 68,
        friction: 8,
      }),
    ]).start();
  };

  return (
    <View
      style={[
        styles.container,
        isOpen ? { width: openContainerWidth, height: openContainerHeight } : styles.closedContainer,
        style,
      ]}
    >
      {/* Backdrop */}
      {isOpen && (
        <View
          style={styles.backdrop}
          pointerEvents="none"
        />
      )}
      
      {/* Action Buttons */}
      {isOpen && actions.map((action, index) => {
        const opacity = animation.interpolate({
          inputRange: [0, 0.5, 1],
          outputRange: [0, 0, 1],
        });

        const isExpanded = expandedCategoryIndex === index;

        return (
          <React.Fragment key={action.id}>
            {/* Horizontal Sub-Actions */}
            {isExpanded && action.subActions && action.subActions.map((subAction, subIndex) => {
              const translateX = horizontalAnimation.interpolate({
                inputRange: [0, 1],
                outputRange: [0, -(60 * (subIndex + 1))],
              });

              const subOpacity = horizontalAnimation.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0, 0, 1],
              });

              const subScale = horizontalAnimation.interpolate({
                inputRange: [0, 1],
                outputRange: [0.8, 1],
              });

              return (
                <AnimatedTouchableOpacity
                  key={subAction.id}
                  testID={`expandable-fab-sub-action-${subAction.id}`}
                  accessibilityRole="button"
                  accessibilityLabel={subAction.label}
                  activeOpacity={0.8}
                  onPress={() => handleSubActionPress(subAction)}
                  style={[
                    styles.subActionButton,
                    styles.actionFAB,
                    {
                      bottom: 70 * (index + 1),
                      transform: [{ translateX }, { scale: subScale }],
                      opacity: subOpacity,
                    },
                  ]}
                >
                  <MaterialCommunityIcons
                    name={subAction.icon}
                    size={24}
                    color="#ffffff"
                    pointerEvents="none"
                  />
                </AnimatedTouchableOpacity>
              );
            })}

            {/* Main Action Button */}
            <AnimatedTouchableOpacity
              testID={`expandable-fab-action-${action.id}`}
              accessibilityRole="button"
              accessibilityLabel={action.label}
              activeOpacity={0.8}
              onPress={() => handleActionPress(action, index)}
              style={[
                styles.actionButton,
                styles.actionFAB,
                {
                  bottom: 70 * (index + 1),
                  opacity,
                },
              ]}
            >
              <MaterialCommunityIcons
                name={action.icon}
                size={24}
                color="#ffffff"
                pointerEvents="none"
              />
            </AnimatedTouchableOpacity>
          </React.Fragment>
        );
      })}

      {/* Main FAB */}
      <TouchableOpacity
        testID="expandable-fab-main"
        onPress={toggleMenu}
        style={[styles.mainFAB, isOpen && styles.mainFABOpen]}
        activeOpacity={0.8}
      >
        <MaterialCommunityIcons
          name={isOpen ? 'close' : 'menu'}
          size={24}
          color="#ffffff"
        />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    margin: spacing.md,
    zIndex: 10,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  closedContainer: {
    width: 56,
    height: 56,
  },
  backdrop: {
    position: 'absolute',
    top: -(screenHeight),
    left: -(screenWidth),
    width: screenWidth * 2,
    height: screenHeight * 2,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: -1,
  },
  mainFAB: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 6,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.27,
    shadowRadius: 4.65,
    shadowColor: '#000',
    zIndex: 101,
  },
  mainFABOpen: {
    backgroundColor: colors.error,
  },
  actionButton: {
    position: 'absolute',
    bottom: 0,
    right: 4,
    zIndex: 102,
  },
  actionFAB: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.23,
    shadowRadius: 2.62,
  },
  subActionButton: {
    position: 'absolute',
    right: 4,
    zIndex: 103,
  },
});
